import { useEffect, useRef } from "react"
import { useStore } from "../../stores/store"
import { canvasStore, closeNewAgentModal } from "../../stores/canvasStore"
import { conversationStore, clearChat } from "../../stores/conversationStore"
import { useTranslations } from "../../lib/translations"
import { ChatTab } from "../AgentModal/ChatTab"

// NewAgentModal — dedicated design surface for creating a new agent.
// Opens off the canvas + button. Wraps the same ChatTab against the
// company's agent-builder, but in a distinct chrome:
//   - LEFT (~58%): the chat itself.
//   - RIGHT (~42%): the "Design preview" — shows the role/name/tools/
//     etc. as the agent-builder collects them. v1 is a static
//     placeholder; v1.5 will reflect the latest pending
//     submit_for_approval payload.
// Header has a Clear-chat button that calls the platform's reset
// endpoint (writes a [CHAT_RESET] marker — see
// Runtime::ResetConversation).
//
// Closes only via the X button or Escape. No scrim-click close.

export function NewAgentModal() {
  const t = useTranslations()
  const open    = useStore(canvasStore, (s) => s.newAgentModalOpen)
  const agents  = useStore(canvasStore, (s) => s.agents)
  const builder = agents.find((a) => a.role === "agent-builder")
  const modalRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === "Escape") closeNewAgentModal() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open])

  if (!open || !builder) return null

  const onClear = async () => {
    // No confirm dialog — the action is reversible-ish (history stays
    // in the audit log, just hidden from this chat view). If the user
    // misclicked they re-explain in the next message. Browser confirms
    // are an unpleasant UX and block subsequent automation.
    try { await clearChat(builder.id) }
    catch (e) { alert(e.message) }
  }

  return (
    <div className="cm-scrim">
      <div className="cm-newagent-modal" ref={modalRef}>
        <div className="cm-newagent-head">
          <div className="cm-newagent-title">
            <h3>{t("new_agent.title")}</h3>
            <small>{t("new_agent.subtitle")}</small>
          </div>
          <button className="cm-newagent-clear" type="button" onClick={onClear}
                  title={t("new_agent.clear.title")}>
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path d="M5 6h14v2H5zm2 3h10v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9zm3-5h4v2h-4z" />
            </svg>
            {t("new_agent.clear.label")}
          </button>
          <div className="cm-x" onClick={closeNewAgentModal} title={t("modal.close")}>✕</div>
        </div>
        <div className="cm-newagent-body">
          <div className="cm-newagent-chat">
            <ChatTab agent={builder} />
          </div>
          <DesignPreview agentBuilderId={builder.id} />
        </div>
      </div>
    </div>
  )
}

// Design preview pane — v1 is a static placeholder that explains the
// flow. v1.5 will subscribe to the agent-builder's pending Tasks /
// approvals and render the most-recent submit_for_approval payload
// as a live "this is what we're about to ship" preview.
function DesignPreview() {
  const t = useTranslations()
  return (
    <aside className="cm-newagent-preview">
      <h4>{t("new_agent.preview.title")}</h4>
      <p className="cm-newagent-hint">{t("new_agent.preview.hint")}</p>
      <ul className="cm-newagent-slots">
        <li><span>{t("new_agent.preview.fields.role")}</span><em>{t("new_agent.preview.unfilled")}</em></li>
        <li><span>{t("new_agent.preview.fields.name")}</span><em>{t("new_agent.preview.unfilled")}</em></li>
        <li><span>{t("new_agent.preview.fields.tools")}</span><em>{t("new_agent.preview.unfilled")}</em></li>
        <li><span>{t("new_agent.preview.fields.credentials")}</span><em>{t("new_agent.preview.unfilled")}</em></li>
        <li><span>{t("new_agent.preview.fields.sign_off")}</span><em>{t("new_agent.preview.unfilled")}</em></li>
      </ul>
      <p className="cm-newagent-foot">{t("new_agent.preview.foot")}</p>
    </aside>
  )
}
